import { useEditorStateStore } from "@/context/editorStateStore";
import { Button } from "@acme/ui/button";
import { ContextMenu } from "@acme/ui/context-menu";
import { Input } from "@acme/ui/input";
import { useEffect, useRef, useState } from "react";
import {
	ControlledTreeEnvironment,
	Tree,
	type TreeEnvironmentRef,
	type TreeItem,
	type TreeItemIndex,
} from "react-complex-tree";
import { LuFilePlus, LuFolderPlus } from "react-icons/lu";
import { FileNode, FileNodeTitle } from "./FileNode";
import { TreeContext } from "./TreeContext";

export interface FilePickerProps {
	onFileSelected: (path: string) => void;
}

export const FilePicker: React.FC<FilePickerProps> = ({
	onFileSelected = () => {},
}) => {
	const { yfs } = useEditorStateStore();

	const treeEnv = useRef<TreeEnvironmentRef>(null);

	const [treeData, setTreeData] = useState<Record<TreeItemIndex, TreeItem>>(
		yfs?.toReactComplexTree() ?? {},
	);
	const [focusedItem, setFocusedItem] = useState<TreeItemIndex>();
	const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>([]);
	const [selectedItems, setSelectedItems] = useState<TreeItemIndex[]>([]);

	if (!yfs) {
		return <></>;
	}

	// Keep tree data in sync with file system changes
	useEffect(() => {
		const observer = () => {
			setTreeData(yfs.toReactComplexTree());
		};

		yfs.fsDoc.observe(observer);

		return () => {
			yfs.fsDoc.unobserve(observer);
		};
	}, [yfs]);

	return (
		<TreeContext.Provider
			value={{ treeRef: treeEnv.current, treeId: "editorFileTree" }}
		>
			<div className="directory">
				<Button
					variant="ghost"
					size="icon"
					onClick={() => {
						const fileName = prompt("Enter the name of the new file:");
						if (fileName) {
							if (!yfs.nodeExists(fileName)) {
								yfs.writeFile(fileName, "");
							}
						}
					}}
				>
					<LuFilePlus />
				</Button>
				<Button
					variant="ghost"
					size="icon"
					onClick={() => {
						const fileName = prompt("Enter the name of the new directory:");
						if (fileName) {
							if (!yfs.nodeExists(fileName)) {
								yfs.mkdir(fileName);
							}
						}
					}}
				>
					<LuFolderPlus />
				</Button>
				<ContextMenu>
					<ControlledTreeEnvironment
						ref={treeEnv}
						items={treeData}
						getItemTitle={(item) => item?.data}
						viewState={{
							editorFileTree: {
								focusedItem,
								expandedItems,
								selectedItems,
							},
						}}
						onFocusItem={(item) => setFocusedItem(item.index)}
						onExpandItem={(item) =>
							setExpandedItems([...expandedItems, item.index])
						}
						onCollapseItem={(item) =>
							setExpandedItems(
								expandedItems.filter(
									(expandedItemIndex) => expandedItemIndex !== item.index,
								),
							)
						}
						onSelectItems={(items) => {
							for (const item of items) {
								if (!treeData[item]?.isFolder) {
									// react-complex-tree thinks these are numbers
									onFileSelected(item as string);
								}
							}
							setSelectedItems(items);
						}}
						onRenameItem={(item, newName) => {
							const newPath = (item.index as string).replace(/[^/]+$/, newName);
							yfs.rename(item.index as string, newPath);

							// Update the tree data
							setTreeData(yfs.toReactComplexTree());
						}}
						renderItem={(props) => <FileNode {...props} />}
						renderItemTitle={(props) => <FileNodeTitle {...props} />}
						renderItemsContainer={({ children, containerProps }) => (
							<ul className="flex flex-col gap-1 mt-1" {...containerProps}>
								{children}
							</ul>
						)}
						renderTreeContainer={({ children, containerProps }) => (
							<div className="p-2" {...containerProps}>
								{children}
							</div>
						)}
						// We don't want the arrows
						renderItemArrow={() => <></>}
						renderRenameInput={({ item, inputProps, inputRef }) => {
							const [value, setValue] = useState(item.data);
							return (
								<Input
									{...inputProps}
									value={value}
									onChange={(e) => setValue(e.target.value)}
									onBlur={() => {
										treeEnv.current?.renameItem(
											item.index,
											value,
											"editorFileTree",
										);
									}}
								/>
							);
						}}
					>
						<Tree
							treeId="editorFileTree"
							rootItem="root"
							treeLabel="Tree Example"
						/>
					</ControlledTreeEnvironment>
				</ContextMenu>
			</div>
		</TreeContext.Provider>
	);
};
